import { ClipboardListIcon } from "lucide-react";

import { DetailField, DetailGrid, DetailSection } from "@/components/show";
import { MatchEventsTimeline } from "@/pages/matches/components/details/match-events-timeline";
import { MatchStatistics } from "@/pages/matches/components/details/match-statistics";
import type { MatchSides } from "@/pages/matches/components/details/sides";
import { formatScore } from "@/pages/matches/lib/match-format";
import type { IMatchReport } from "@/types/match-details";

/** Whether the report has anything to show: an empty block renders nothing, not an empty card. */
export function reportHasData(report?: IMatchReport | null): boolean {
  if (!report) return false;
  return (
    report.score?.home != null ||
    !!report.events?.length ||
    !!report.statistics?.length ||
    !!report.referee?.name ||
    typeof report.venueCapacity === "number"
  );
}

const scoreText = (score: NonNullable<IMatchReport["score"]>) => {
  const full = formatScore(score.home, score.away);
  const pens = score.penalties;
  return pens?.home != null && pens.away != null
    ? `${full} (penalties ${formatScore(pens.home, pens.away)})`
    : full;
};

/**
 * The post-match report: score, referee, the ground's capacity, the events timeline and the
 * statistics. Each part shows only when the report carries it (a sport without an event
 * feed has no events).
 */
export function MatchReportCard({
  report,
  sides,
}: {
  report: IMatchReport;
  sides: MatchSides;
}) {
  const events = report.events ?? [];
  const stats = report.statistics ?? [];
  const referee = report.referee?.name
    ? [report.referee.name, report.referee.nationality]
        .filter(Boolean)
        .join(" · ")
    : undefined;
  const hasScore = report.score?.home != null && report.score?.away != null;
  const capacity =
    typeof report.venueCapacity === "number" ? report.venueCapacity : undefined;

  return (
    <DetailSection
      title="Report"
      icon={<ClipboardListIcon className="size-4" />}
      contentClassName="flex flex-col gap-6"
    >
      {(hasScore || referee || capacity !== undefined) && (
        <DetailGrid className="sm:grid-cols-3">
          {hasScore && report.score && (
            <DetailField label="Final score" value={scoreText(report.score)} />
          )}
          {referee && <DetailField label="Referee" value={referee} />}
          {capacity !== undefined && (
            <DetailField
              label="Venue capacity"
              value={capacity.toLocaleString("en-US")}
            />
          )}
        </DetailGrid>
      )}
      {(events.length > 0 || stats.length > 0) && (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {events.length > 0 && (
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">Events</h3>
              <MatchEventsTimeline
                events={events}
                homeTeamId={sides.homeTeamId}
              />
            </div>
          )}
          {stats.length > 0 && (
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">Statistics</h3>
              <MatchStatistics stats={stats} sides={sides} />
            </div>
          )}
        </div>
      )}
    </DetailSection>
  );
}
